import { api } from "../httpClient";
import { API_ENDPOINTS } from "../config";

/**
 * Search for available cars
 * @param {Object} searchParams - Search parameters
 * @returns {Promise<Object>} Search results
 */
// export const searchCars = async (searchParams) => {
//   try {
//     const response = await api.post(API_ENDPOINTS.SEARCH_CARS, searchParams);
//     return response;
//   } catch (error) {
//     console.error("Error searching cars:", error);
//     throw new Error("Failed to search for cars");
//   }
// };

/**
 * Get car details by ID
 * @param {string} carId - Car ID
 * @param {Object} searchParams - Original search parameters
 * @returns {Promise<Object>} Car details
 */
export const getCarDetails = async (carId, searchParams) => {
  try {
    const response = await api.post(
      `${API_ENDPOINTS.CAR_DETAILS}/${carId}`,
      searchParams
    );
    return response;
  } catch (error) {
    console.error("Error fetching car details:", error);
    throw new Error("Failed to fetch car details");
  }
};

/**
 * Get car categories/types
 * @returns {Promise<Array>} Array of car categories
 */
export const getCarCategories = async () => {
  try {
    const response = await api.get(API_ENDPOINTS.FILTER_METADATA);
    return response?.data?.carCategories || [];
  } catch (error) {
    console.error("Error fetching car categories:", error);
    return [];
  }
};

/**
 * Get car vendors/suppliers
 * @returns {Promise<Array>} Array of car vendors
 */
export const getCarVendors = async () => {
  try {
    const response = await api.get(API_ENDPOINTS.FILTER_METADATA);
    return response?.data?.carVendors || [];
  } catch (error) {
    console.error("Error fetching car vendors:", error);
    return [];
  }
};

/**
 * Get car rates based on search parameters
 * @param {Object} searchParams - Search parameters
 * @returns {Promise<Object>} Car rates data
 */
// export const getCarRates = async (searchParams) => {
//   try {
//     // Get selected currency from localStorage
//     let selectedCurrency = 'USD'; // Default fallback
//     try {
//       const savedCurrency = localStorage.getItem('selectedCurrency');
//       if (savedCurrency) {
//         const parsedCurrency = JSON.parse(savedCurrency);
//         selectedCurrency = parsedCurrency.code;
//       }
//     } catch (error) {
//       console.error('Error parsing saved currency:', error);
//     }

//     // Add selectedCurrency to the payload
//     const payloadWithCurrency = {
//       ...searchParams,
//       selectedCurrency: selectedCurrency
//     };

//     const response = await api.post(API_ENDPOINTS.CAR_RATES, payloadWithCurrency);
//     return response;
//   } catch (error) {
//     console.error("Error fetching car rates:", error);
//     throw new Error("Failed to fetch car rates");
//   }
// };

/**
 * Filter cars by criteria
 * @param {Array} cars - Array of cars to filter
 * @param {Object} filters - Filter criteria
 * @returns {Array} Filtered cars
 */
export const filterCars = (cars, filters = {}) => {
  if (!cars || !Array.isArray(cars)) return [];

  return cars.filter((car) => {
    // Filter by vendor
    if (filters.vendor && car.vendor !== filters.vendor) {
      return false;
    }

    // Filter by car class
    if (filters.carClass && car.carClass !== filters.carClass) {
      return false;
    }

    // Filter by price range
    if (filters.minPrice && car.price < filters.minPrice) {
      return false;
    }
    if (filters.maxPrice && car.price > filters.maxPrice) {
      return false;
    }

    // Filter by transmission
    if (filters.transmission && car.transmission !== filters.transmission) {
      return false;
    }

    // Filter by fuel type
    if (filters.fuelType && car.fuelType !== filters.fuelType) {
      return false;
    }

    return true;
  });
};

/**
 * Sort cars by criteria
 * @param {Array} cars - Array of cars to sort
 * @param {string} sortBy - Sort criteria (price, name, rating)
 * @param {string} sortOrder - Sort order (asc, desc)
 * @returns {Array} Sorted cars
 */
export const sortCars = (cars, sortBy = "price", sortOrder = "asc") => {
  if (!cars || !Array.isArray(cars)) return [];

  return [...cars].sort((a, b) => {
    let aValue, bValue;

    switch (sortBy) {
      case "price":
        aValue = parseFloat(a.price) || 0;
        bValue = parseFloat(b.price) || 0;
        break;
      case "name":
        aValue = a.name || "";
        bValue = b.name || "";
        break;
      case "rating":
        aValue = parseFloat(a.rating) || 0;
        bValue = parseFloat(b.rating) || 0;
        break;
      default:
        aValue = a[sortBy] || "";
        bValue = b[sortBy] || "";
    }

    if (sortOrder === "desc") {
      return bValue > aValue ? 1 : -1;
    }
    return aValue > bValue ? 1 : -1;
  });
};

/**
 * Get location policy information
 * @param {Object} params - Location parameters
 * @returns {Promise<Object>} Location policy data
 */
export const getLocationPolicyInfo = async (params) => {
  try {
    const response = await api.post("/common/locationpolicyinfo", params);
    return response;
  } catch (error) {
    console.error("Error fetching location policy info:", error);
    throw new Error("Failed to fetch location policy information");
  }
};


//===============================================================================================
//NEW DATA MODEL API
export const getCarRates = async (apiPayload) => {
  try {
    // We already built the JSON in Form.js, so just send it!
    const response = await api.post(API_ENDPOINTS.AVAILABILITY_SEARCH, apiPayload);
    
    // response is already response.data thanks to your interceptor
    return response; 
  } catch (error) {
    console.error("Error in getCarRates API call:", error);
    throw error;
  }
};

// Alias for backward compatibility to fix the export error
export const searchCars = getCarRates;

/**
 * Get single car offer by searchId + offerHash
 */
export const getCarOffer = async ({ searchId, offerHash }) => {
  try {
    const response = await api.get(
      `${API_ENDPOINTS.CAR_OFFER_DETAILS}?searchId=${searchId}&carOfferHash=${offerHash}`
    );
    return response;
  } catch (error) {
    console.error("Error fetching car offer:", error);
    throw error;
  }
};

/**
 * Register a new search and get back a search_id
 * @param {Object} apiPayload - Full search payload including currency
 * @returns {Promise<Object>} Object containing search_id
 */
export const registerSearch = async (apiPayload) => {
  try {
    const response = await api.post(
      API_ENDPOINTS.AVAILABILITY_REGISTER_SEARCH,
      apiPayload
    );
    return response;
  } catch (error) {
    console.error("Error in registerSearch API call:", error);
    throw error;
  }
};

/**
 * Fetch search results by search_id
 * @param {string} searchId - The search_id returned by registerSearch
 * @returns {Promise<Object>} Same response structure as getCarRates
 */
export const searchById = async (searchId) => {
  try {
    const response = await api.get(
      `${API_ENDPOINTS.AVAILABILITY_SEARCH_BY_ID}?searchId=${searchId}`
    );
    return response;
  } catch (error) {
    console.error("Error in searchById API call:", error);
    throw error;
  }
};
